import { z } from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

export const agentRoles = ['song-agent', 'project-agent', 'folder-agent', 'generic'] as const
export type AgentRole = (typeof agentRoles)[number]
export const agentRoleSchema = z.enum(agentRoles)

export const agentStatuses = ['active', 'paused', 'archived'] as const
export type AgentStatus = (typeof agentStatuses)[number]
export const agentStatusSchema = z.enum(agentStatuses)

/**
 * Live runtime state. Orthogonal to the persistent `status` field
 * (which is the user's intent — `active` / `paused` / `archived`).
 * `runtimeStatus` is "is the agent currently executing a turn?" and
 * is broadcast via WebSocket partial-entity updates without touching
 * DynamoDB. Default-undefined means idle.
 */
export const agentRuntimeStatuses = ['idle', 'thinking', 'error'] as const
export type AgentRuntimeStatus = (typeof agentRuntimeStatuses)[number]
export const agentRuntimeStatusSchema = z.enum(agentRuntimeStatuses)

export const agentProactivityLevels = ['off', 'low', 'normal', 'high'] as const
export type AgentProactivity = (typeof agentProactivityLevels)[number]
export const agentProactivitySchema = z.enum(agentProactivityLevels)

export const agentTriggerConfigSchema = z.object({
  onChange: z.boolean().optional(),
  idleDays: z.number().int().positive().optional(),
  sessionSignals: z.boolean().optional(),
  goals: z.boolean().optional(),
})

export const agentConfigSchema = z.object({
  proactivity: agentProactivitySchema.default('normal'),
  triggers: agentTriggerConfigSchema.default({}),
  allowedTools: z.array(z.string()).optional(),
})

export const agentGoalSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  dueAt: z.string().describe('ISO-8601 deadline').optional(),
  completed: z.boolean().optional(),
  createdAt: z.string().describe('ISO-8601 creation timestamp'),
})
export type AgentGoal = z.infer<typeof agentGoalSchema>

export const agentMemorySchema = z.object({
  summary: z.string().optional(),
  facts: z.array(z.string()).optional(),
  openQuestions: z.array(z.string()).optional(),
})
export type AgentMemory = z.infer<typeof agentMemorySchema>

export const agentSchema = baseEntitySchema.extend({
  type: z.literal('agent'),
  /** Links to the AgentDefinition.id from the plugin that created this agent */
  definitionId: z.string().optional(),
  /** Custom role line for the system prompt preamble. Overrides role-based default. */
  roleLine: z.string().optional(),
  role: agentRoleSchema,
  status: agentStatusSchema.default('active'),
  /** Model id override. Stored as a plain string so schemas/ stays independent of ai-lib. */
  model: z.string().optional(),
  config: agentConfigSchema,
  goals: z.array(agentGoalSchema).optional(),
  memory: agentMemorySchema.optional(),
  /** ISO-8601 timestamp of the most recent proactive run. Used to rate-limit nudges. */
  lastRunAt: z.string().optional(),
  /** ISO-8601 timestamp of the most recent user-visible nudge. */
  lastNudgeAt: z.string().optional(),
  /**
   * Live runtime state — transient, never persisted to DDB.
   * Pushed via WebSocket `receivePartialEntity` envelopes. Default-undefined
   * means idle. See `agentRuntimeStatuses` for axis docs.
   */
  runtimeStatus: agentRuntimeStatusSchema.describe('Transient runtime state').optional(),
  /** ISO-8601 timestamp the runtimeStatus was last asserted. Used for client-side TTL. */
  runtimeStatusAt: z.string().describe('ISO-8601 of last runtime status assert').optional(),
  /** Human-readable error blurb when runtimeStatus === 'error'. */
  runtimeError: z.string().describe('Error blurb for runtimeStatus error').optional(),
})

export type Agent = z.infer<typeof agentSchema>

export function isAgent(node?: unknown): node is Agent {
  return agentSchema.safeParse(node).success
}
